import { HeadContent, Link, Outlet, createRootRoute } from '@tanstack/react-router';
import { Toaster } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { t } from '@/i18n/index';

/**
 * The app shell. Every page shares the tokens and the toaster; the public site adds its
 * header and footer in `_site.tsx`, the session at `/play` and the backoffice at
 * `/admin` draw their own chrome, because a session is a full-height screen and the
 * backoffice is a tool, not a page.
 */
export const Route = createRootRoute({
  head: () => ({
    meta: [
      { title: 'Everwake — Speak, and the world wakes' },
      {
        name: 'description',
        content: 'A voice-first role-playing game whose game master is an AI. 5E-compatible, adults only.',
      },
      { property: 'og:site_name', content: 'Everwake' },
      { property: 'og:type', content: 'website' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
  }),
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: ErrorPage,
});

function RootComponent() {
  return (
    <>
      <HeadContent />
      <Outlet />
      <Toaster position="bottom-center" />
    </>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p className="rule-line">404</p>
        <h1 className="mt-3 font-display text-3xl">{t('app.not_found.title')}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t('app.not_found.body')}</p>
        <Button asChild className="mt-6">
          <Link to="/">{t('app.not_found.home')}</Link>
        </Button>
      </div>
    </div>
  );
}

function ErrorPage({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-2xl">{t('app.error.title')}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t('app.error.body')}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <Button onClick={reset}>{t('app.error.retry')}</Button>
          <Button asChild variant="outline">
            <a href="/">{t('app.error.home')}</a>
          </Button>
        </div>
      </div>
    </div>
  );
}
